// ─── PlacesAutocompleteInput ──────────────────────────────────────────────
// Drop-in replacement for a plain <input> that mounts a Google Places
// Autocomplete dropdown on top of it. Requires the Google Maps JavaScript
// API to be loaded with the `places` library (App.js does this).
//
// Props:
//   value / onChange   — controlled value like a normal input
//   onSelect(payload)  — fires when the user picks a suggestion. Payload:
//                          { address, lat, lng, place }
//   country            — restrict suggestions to this ISO code (default "us")
//   types              — Places autocomplete types (default ["address"])
//   ...rest            — anything else (className, placeholder, style) is
//                        passed straight to the underlying <input>
//
// Falls back to a plain input if the Google JS API hasn't loaded yet, so
// dev mode without a key still works.

import { useEffect, useRef } from "react";

export default function PlacesAutocompleteInput({
  value = "",
  onChange,
  onSelect,
  country = "us",
  types = ["address"],
  inputRef,
  ...rest
}) {
  const localRef = useRef(null);
  const ref = inputRef || localRef;
  const acRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    let cancelled = false;
    let listener = null;

    // The Maps JS API is loaded async; poll briefly until google.maps.places
    // shows up. Bail after 5s to avoid a runaway.
    const start = Date.now();
    const wait = () => {
      if (cancelled) return;
      if (window.google?.maps?.places) {
        try {
          acRef.current = new window.google.maps.places.Autocomplete(ref.current, {
            types,
            componentRestrictions: country ? { country } : undefined,
            fields: ["formatted_address", "geometry.location", "address_components", "name", "place_id"],
          });
          listener = acRef.current.addListener("place_changed", () => {
            const place = acRef.current.getPlace();
            const address = place.formatted_address || place.name || "";
            const lat = place.geometry?.location?.lat?.();
            const lng = place.geometry?.location?.lng?.();
            onChange?.(address);
            onSelect?.({ address, lat, lng, place });
          });
        } catch (e) { console.warn("PlacesAutocomplete init failed:", e); }
      } else if (Date.now() - start < 5000) {
        setTimeout(wait, 150);
      }
    };
    wait();

    return () => {
      cancelled = true;
      if (listener) { try { listener.remove(); } catch {} }
      if (acRef.current) { try { window.google?.maps?.event?.clearInstanceListeners?.(acRef.current); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      {...rest}
    />
  );
}
